import * as React from 'react';
import Modal from '../../../misc/components/modal';
import { OverreadStatusIdEnum } from '../../../misc/api/apiTypes';
import { SubmitState } from '../CoughSessionsListReducer';
import { SelectPrimaryAnalysisSetForm } from './SelectPrimaryAnalysisSetsForm';
import { AuditModalBody } from '../../../auditmodal/components/AuditModalBody';
import LoadingWithMessage from '../../../misc/components/LoadingWithMessage';
import { CoughAnalysisSet, CoughSession } from 'webportalclient';
import { useWebPortalApiClient } from '../../../misc/api/WebPortalTokenClient';

export interface PrimaryAnalysisSetModalProps {
    session: CoughSession;
    onClose: () => void;
}

export const PrimaryAnalysisSetModal = (props: PrimaryAnalysisSetModalProps) => {
    const [error, setError] = React.useState(false);
    const client = useWebPortalApiClient();
    const [submitting, setSubmitting] = React.useState<SubmitState>(SubmitState.Loading);
    const [primarySets, setPrimarySets] = React.useState<CoughAnalysisSet[]>([]);

    React.useEffect(() => {
        const getExisting = async () => {
            setSubmitting(SubmitState.Loading);
            const coughSessionResult = await client.client.getCoughSession(props.session.id, "id,primaryAnalysisSetIds,analysisSets", "analysisSets($select=id,typeName,created,statusName)");

            setPrimarySets(coughSessionResult.analysisSets.filter(a => coughSessionResult.primaryAnalysisSetIds.includes(a.id)));
            setSubmitting(SubmitState.Form);
        }

        getExisting();
    }, [props.session.id, client.client]);

    const handleChange = (v) => {
        setPrimarySets(v);
    }

    const handleSubmit = async (auditForm) => {
        const auditAnnotation = auditForm.Annotation;
        setSubmitting(SubmitState.Submitting);
        if (primarySets.length === 0) {
            setError(true);
        } else {
            setError(false);

            const patchResult = await client.client.patchCoughSession({
                primaryAnalysisSetIds: primarySets.map(ps => ps.id),
            }, props.session.id,
                auditAnnotation
            );

            props.onClose();
        }
    }

    const handleSubmitForAudit = () => {
        if (primarySets.length === 0) {
            setError(true);
        } else {
            setError(false);
            
            setSubmitting(SubmitState.AuditTrail);
        }
    }

    const possibleAnalysisSetsSelector = (session: CoughSession): Array<CoughAnalysisSet> =>
        session.analysisSets.filter((x: CoughAnalysisSet) => !x.isLinkedAnalysisSet && (x.status === OverreadStatusIdEnum.Accepted || x.status === OverreadStatusIdEnum.Rejected))


    if (props.session.id) {
        let form;
        switch (submitting) {
            case SubmitState.Loading:
                form = (<div className="modal-body">
                    <LoadingWithMessage message="Loading Primary Analysis Sets..." />
                </div>)
                break;
            case SubmitState.Form:
                form = <SelectPrimaryAnalysisSetForm
                    showError={error}
                    analysisSetOptions={possibleAnalysisSetsSelector(props.session)}
                    selectedAnalysisSets={primarySets}
                    onSubmit={() => handleSubmitForAudit()}
                    onChange={handleChange}
                    onClose={() => props.onClose()}
                />;
                break;
            case SubmitState.AuditTrail:
                form = <AuditModalBody
                    isSubmitting={false}
                    onClose={() => props.onClose()}
                    onSubmit={(auditForm) => handleSubmit(auditForm)}
                >
                </AuditModalBody>
                break;
            case SubmitState.Submitting:
                form = (<div className="modal-body">
                    <LoadingWithMessage message="Setting Primary Analysis Sets..." />
                </div>)
                break;
        }

        return (
            <Modal show={true} closeModalHandler={() => props.onClose()} header="Session Primary Analysis Sets">
                {form}
            </Modal>
        );
    } else {
        return null;
    }
}

